import { useClubSlug } from 'components/Clubs/hooks';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Mandant } from '../types';

interface ActiveMandantState {
    byClub: Record<string, number>;
    select: (clubSlug: string, mandantId: number) => void;
}

export const useActiveMandantStore = create<ActiveMandantState>()(
    persist(
        (set) => ({
            byClub: {},
            select: (clubSlug, mandantId) => set((state) => ({ byClub: { ...state.byClub, [clubSlug]: mandantId } }))
        }),
        { name: 'user-invoice.active-mandant.v1' }
    )
);

export const mandantenKey = (clubSlug?: string | null) => (clubSlug ? `invoice/${clubSlug}/mandant` : null);

export const useMandanten = (clubSlug?: string | null) => {
    const { data, isLoading, mutate } = useSWR<{ data?: Mandant[] }>(mandantenKey(clubSlug), {
        refreshInterval: (latest) => (latest?.data?.some((m) => m.conversion_running) ? 5000 : 0)
    });
    const mandanten = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);

    return { mandanten, isLoading, mutate };
};

/**
 * The Mandant the accounting screens work in. Resolved from `?mandant=` in the URL, then the
 * last choice per club, then the club's oldest Mandant. A stored id the club no longer has is
 * ignored, so a deleted Mandant never leaves the screens pointing at nothing.
 */
export const useActiveMandant = (clubSlugProp?: string | null) => {
    const clubSlug = useClubSlug(clubSlugProp ?? undefined);
    const { mandanten, isLoading, mutate } = useMandanten(clubSlug);
    const { query, isReady, replace } = useRouter();
    const storedId = useActiveMandantStore((state) => (clubSlug ? state.byClub[clubSlug] : undefined));
    const selectInStore = useActiveMandantStore((state) => state.select);

    const routeId = isReady ? Number(query.mandant) || undefined : undefined;

    const mandant = useMemo(() => {
        if (mandanten.length === 0) return undefined;

        return (
            mandanten.find((m) => m.id === routeId) ??
            mandanten.find((m) => m.id === storedId) ??
            mandanten.reduce((lowest, m) => (m.id < lowest.id ? m : lowest))
        );
    }, [mandanten, routeId, storedId]);

    const setMandantId = useCallback(
        (mandantId: number) => {
            if (!clubSlug) return;

            selectInStore(clubSlug, mandantId);

            if (routeId) {
                void replace({ query: { ...query, mandant: String(mandantId) } }, undefined, { shallow: true });
            }
        },
        [clubSlug, query, replace, routeId, selectInStore]
    );

    return {
        mandant,
        mandanten,
        mandantId: mandant?.id,
        setMandantId,
        isLoading: !isReady || isLoading,
        mutate
    };
};

export default useActiveMandant;
